import { type LucideIcon, Mail, Send } from 'lucide-react';

import { ToggleGroup, ToggleGroupItem } from 'components/ToggleGroup';
import { Flex } from 'components/layout';
import { Typography } from 'components/typography';
import { cn } from 'utils/shadcn';

export enum BidFlowMode {
  DraftEmail = 'draft_email',
  SubmitBid = 'submit_bid',
}

interface BidFlowToggleProps {
  value: BidFlowMode;
  onValueChange: (value: BidFlowMode) => void;
}

const TOGGLE_ITEM_CLASSNAME = cn(
  'relative z-10 flex flex-1 h-8 min-h-0 items-center justify-center gap-1.5 py-0 px-2',
  '!bg-transparent border-0 shadow-none text-xs font-medium',
  'data-[state=on]:!bg-transparent data-[state=off]:!bg-transparent',
  'data-[state=on]:text-neutral-50 data-[state=off]:text-neutral-600',
  'data-[state=on]:[&>svg]:stroke-neutral-50! data-[state=off]:[&>svg]:stroke-neutral-600!',
  'hover:!bg-transparent hover:text-neutral-600 hover:data-[state=off]:[&>svg]:stroke-neutral-600!',
  'data-[state=on]:hover:text-neutral-50 data-[state=on]:hover:[&>svg]:stroke-neutral-50!'
);

const SLIDE_LEFT: Record<BidFlowMode, string> = {
  draft_email: 'left-0.5',
  submit_bid: 'left-[calc(50%+1px)]',
};

const OPTIONS: ReadonlyArray<{
  value: BidFlowMode;
  label: string;
  Icon: LucideIcon;
}> = [
  { value: BidFlowMode.DraftEmail, label: 'Draft Email', Icon: Mail },
  { value: BidFlowMode.SubmitBid, label: 'Submit Bid', Icon: Send },
];

export function BidFlowToggle({ value, onValueChange }: BidFlowToggleProps) {
  return (
    <Flex direction='col' className='w-full'>
      <ToggleGroup
        type='single'
        value={value}
        onValueChange={(newValue: string) => {
          if (
            newValue === BidFlowMode.DraftEmail ||
            newValue === BidFlowMode.SubmitBid
          ) {
            onValueChange(newValue as BidFlowMode);
          }
        }}
        className={cn(
          'relative m-0 grid h-9 w-full grid-cols-2 gap-0',
          'rounded-md border border-neutral-300 bg-neutral-100 p-0.5'
        )}
      >
        <div
          className={cn(
            'absolute top-0.5 bottom-0.5 w-[calc(50%-2px)] rounded-[4px] bg-brand-main transition-all duration-200 ease-in-out',
            SLIDE_LEFT[value]
          )}
        />

        {OPTIONS.map(({ value: mode, label, Icon }) => (
          <ToggleGroupItem
            key={mode}
            value={mode}
            aria-label={label}
            className={TOGGLE_ITEM_CLASSNAME}
          >
            <Icon className='h-3.5 w-3.5 shrink-0' aria-hidden />
            <Typography
              as='span'
              variant='body-xs'
              weight='medium'
              className='text-inherit'
            >
              {label}
            </Typography>
          </ToggleGroupItem>
        ))}
      </ToggleGroup>
    </Flex>
  );
}
